import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PRODUCTION_URL = process.env.REACT_APP_PRODUCTION_URL || '';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const token = searchParams.get('token');

  const handleGoToLogin = () => {
    if (PRODUCTION_URL) {
      window.location.href = PRODUCTION_URL;
    } else {
      navigate('/');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Mot de passe réinitialisé avec succès !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: 'var(--color-bg)' }}>
        <div className="card-base p-8 max-w-md w-full text-center">
          <h1 className="text-2xl font-bold mb-4 text-red-600">Lien invalide</h1>
          <p className="text-gray-600 mb-6">Ce lien de réinitialisation est invalide ou a expiré.</p>
          <Button onClick={() => navigate('/')} variant="outline">
            Retour à la connexion
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: 'var(--color-bg)' }}>
        <div className="card-base p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
          <h1 className="text-2xl font-bold mb-2 text-green-600">
            Mot de passe réinitialisé !
          </h1>
          <p className="text-gray-600 mb-6">
            Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
          </p>
          <Button 
            onClick={handleGoToLogin}
            className="btn-primary"
            data-testid="go-to-login-btn"
          >
            Se connecter
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: 'var(--color-bg)' }}>
      <div className="card-base p-8 max-w-md w-full">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm mb-6 hover:underline"
          style={{ color: 'var(--color-primary)' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la connexion
        </button>
        
        <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-primary)' }}>
          Nouveau mot de passe
        </h1>
        <p className="text-gray-600 mb-6">
          Entrez votre nouveau mot de passe ci-dessous.
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="form-group">
            <Label htmlFor="password" className="form-label">Nouveau mot de passe</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-base pr-12"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          <div className="form-group">
            <Label htmlFor="confirm-password" className="form-label">Confirmer le mot de passe</Label>
            <Input
              id="confirm-password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input-base"
              placeholder="••••••••"
              required
              data-testid="confirm-password-input"
            />
          </div>
          
          <Button
            type="submit"
            disabled={loading}
            className="btn-primary w-full h-12"
            data-testid="reset-password-btn"
          >
            {loading ? <span className="spinner" /> : 'Réinitialiser le mot de passe'}
          </Button>
        </form>
      </div>
    </div>
  );
}
